import React from 'react';
import { Typography, Container } from '@mui/material';
import {
  VerifiedUser,
  Lock,
  AdminPanelSettings,
  Badge,
  Security as SecurityIcon,
  Psychology
} from '@mui/icons-material';
import CTASection from '../shared-components/CTASection';
import TrustBadges from '../shared-components/TrustBadges';

function Security() {
  const trustPillars = [
    {
      icon: <Psychology sx={{ fontSize: 40 }} />,
      iconColor: "text-blue-600",
      iconBg: "bg-blue-100",
      title: "Human-in-the-Loop AI",
      description: "Our AI assists with administrative tasks, but all clinical decisions remain with licensed professionals. Technology enhances care—it never replaces human judgment."
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 40 }} />,
      iconColor: "text-green-600",
      iconBg: "bg-green-100",
      title: "Snapshot Data Integrity",
      description: "Complete audit trail for all client and billing data. Every change is tracked, timestamped, and preserved, so you can always see who changed what and when."
    },
    {
      icon: <VerifiedUser sx={{ fontSize: 40 }} />,
      iconColor: "text-purple-600",
      iconBg: "bg-purple-100",
      title: "Cryptographic Signatures",
      description: "Providers digitally sign clinical records with tamper-proof cryptographic signatures, ensuring document authenticity and legal validity."
    },
    {
      icon: <Badge sx={{ fontSize: 40 }} />,
      iconColor: "text-indigo-600",
      iconBg: "bg-indigo-100",
      title: "Identity Verification",
      description: "Optional government ID and selfie verification gives users a trust badge, helping both sides of the mental healthcare relationship verify authenticity."
    },
    {
      icon: <Lock sx={{ fontSize: 40 }} />,
      iconColor: "text-red-600",
      iconBg: "bg-red-100",
      title: "Encryption at Rest and in Transit",
      description: "All data is fully encrypted at rest and in transit using industry-standard protocols. Your sensitive information is protected at every step."
    },
    {
      icon: <AdminPanelSettings sx={{ fontSize: 40 }} />,
      iconColor: "text-orange-600",
      iconBg: "bg-orange-100",
      title: "Granular Access Control",
      description: "Strong user-based access control at the data element level. Users only see what they need to see, when they need to see it."
    }
  ];

  return (
    <div className="min-h-screen">
      {/* HERO SECTION */}
      <section className="pt-24 pb-20 bg-gradient-to-b from-blue-50 to-white">
        <Container maxWidth="lg">
          <div className="text-center max-w-4xl mx-auto">
            <Typography
              variant="h1"
              align="center"
              className="text-5xl lg:text-6xl font-extrabold text-gray-900 mb-6 leading-tight"
            >
              Compliance Focused{' '}<br className="hidden md:block" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                Engineering
              </span>
            </Typography>

            <div className="inline-block mb-6 px-4 py-1.5 rounded-full bg-blue-100 text-blue-700 font-semibold text-sm tracking-wide uppercase">
              Security & Privacy First
            </div>

            <Typography
              variant="h2"
              align="center"
              className="text-xl text-gray-600 mb-10 font-normal leading-relaxed max-w-2xl mx-auto"
            >
              Built for mental healthcare with security, compliance, and trust at the foundation. We never sell your personal data to third parties.
            </Typography>

            <div className="flex items-center justify-center gap-8">
              <TrustBadges />
            </div>
          </div>
        </Container>
      </section>

      {/* TRUST PILLARS SECTION */}
      <section className="py-24 bg-gray-50 shadow-inner">
        <Container maxWidth="lg">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <Typography variant="h2" align="center" className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Six Pillars of Trust
            </Typography>
            <Typography variant="body1" align="center" className="text-xl text-gray-600 leading-relaxed">
              Every aspect of Halespring is designed with security, privacy, and trust as core principles.
            </Typography>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {trustPillars.map((pillar, index) => (
              <div key={index} className="bg-white pt-16 pb-12 px-12 rounded-2xl border border-gray-200 hover:shadow-xl hover:border-blue-200 transition-all duration-300 group">
                <div className="text-center">
                  <div className={`w-20 h-20 rounded-2xl flex items-center justify-center mb-6 mx-auto ${pillar.iconBg} ${pillar.iconColor} group-hover:scale-110 transition-transform duration-300`}>
                    {pillar.icon}
                  </div>
                  <Typography variant="h5" className="font-bold text-gray-900 mb-4 text-center">
                    {pillar.title}
                  </Typography>
                  <Typography variant="body1" className="text-gray-600 leading-relaxed text-lg text-center">
                    {pillar.description}
                  </Typography>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* PRIVACY COMMITMENT CALLOUT */}
      <section className="py-16 bg-blue-600">
        <Container maxWidth="lg">
          <div className="text-center text-white">
            <Typography variant="h3" className="text-3xl font-bold mb-4">
              Your Data Stays Yours
            </Typography>
            <Typography variant="h6" className="text-xl font-normal opacity-90">
              We never sell personal data to third parties. Your privacy is not for sale.
            </Typography>
          </div>
        </Container>
      </section>

      {/* FINAL CTA SECTION */}
      <CTASection
        headline="Ready for Secure Care Coordination?"
        copy="Join mental health professionals who trust Halespring's governance-first approach to protect their practice and their patients."
        primaryCTA="Get Started Free"
        secondaryCTA="View Pricing"
        trustElements={[
          "HIPAA BAA included",
          "Encryption at rest and in transit",
          "No data selling—ever"
        ]}
      />
    </div>
  );
}

export default Security;